import React from 'react';
import styles from './StatusPill.module.css';

/**
 * StatusPill — neutral pill with a colored status dot.
 *
 * Shared primitive for "X is in state Y" labels (table cells, detail
 * panes, list rows, etc.). Replaces per-feature inline implementations
 * (e.g. ProjectsTable's STATUS_PILL + PILL_STYLE constants).
 *
 * Style — auto-flipping pill background (subtle 8% alpha wash via
 * color-mix on --text-primary), --text-primary label color, and a
 * colored dot picked by tone or explicit color override.
 */

type StatusTone = 'success' | 'warning' | 'error' | 'info' | 'neutral';

export interface StatusPillProps {
  /** Preset tone → matching --color-status-* token (or --color-primary-blue
   *  for 'info'). Default 'neutral'. */
  tone?: StatusTone;
  /** Optional explicit dot color (CSS color string or token var). Overrides
   *  `tone` when provided. */
  color?: string;
  /** Text rendered after the dot. */
  label: React.ReactNode;
  /** 'sm' is for dense rows. */
  size?: 'sm' | 'md';
  /** Optional wrapper class for one-off tweaks. */
  className?: string;
}

const TONE_COLOR: Record<StatusTone, string> = {
  success: 'var(--color-status-success)',
  warning: 'var(--color-status-warning)',
  error:   'var(--color-status-error)',
  info:    'var(--color-primary-blue)',
  neutral: 'var(--color-status-neutral)',
};

const StatusPill = ({
  tone = 'neutral',
  color,
  label,
  size = 'md',
  className = '',
}: StatusPillProps) => {
  const dotColor = color ?? TONE_COLOR[tone] ?? TONE_COLOR.neutral;
  return (
    <span
      className={`${styles.pill} ${size === 'sm' ? styles.sm : ''} ${className}`.trim()}
    >
      <span className={styles.dot} style={{ background: dotColor }} aria-hidden="true" />
      <span className={styles.label}>{label}</span>
    </span>
  );
};

export default StatusPill;
export { StatusPill };
